import { useState, useCallback, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  initiateMultipartUpload,
  uploadChunk,
  completeMultipartUpload,
  calculateOptimalChunkSize,
} from '../utils/uploadApi';
import { useProcessingStatus } from './useProcessingStatus';
import { logger } from '../utils/logger';

export interface UploadItem {
  id: string;
  file: File;
  // 'processing-timeout' mirrors useProcessingStatus's TIMEOUT branch so the
  // UI can offer a "still processing — refresh later" affordance distinct from
  // a true 'failed' state.
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'processing-timeout';
  progress: number;
  uploadedBytes: number;
  totalChunks: number;
  completedChunks: number;
  uploadSpeed: number;
  estimatedTimeRemaining: number;
  error?: string;
  uploadId?: string;
  catalogId?: string;
  imageId?: string;
  thumbnailUrl?: string;
  aliCatalogNumber?: string;
}

/** A file paired with a pre-assigned imageId from the caller (e.g., UploadTab). */
export type FileUploadItem = { file: File; imageId: string };

/**
 * Resolves the catalog ID to use for an upload, coordinating catalog creation
 * across concurrent uploads to prevent each image from creating its own catalog.
 *
 * When multiple images are uploaded simultaneously without an existing catalogId,
 * only the first upload should create a new catalog. Subsequent concurrent uploads
 * must wait for and reuse the catalog ID from the first upload (issue #858).
 *
 * @param existingCatalogId - The catalog ID already known (from context or previous uploads)
 * @param createCatalogPromise - A factory that starts the catalog-creating async operation
 * @param pendingPromise - The in-flight promise from a concurrent upload (if any)
 * @returns The resolved catalog ID to use for this upload
 */
export const resolveCatalogId = async (
  existingCatalogId: string | undefined,
  createCatalogPromise: () => Promise<string>,
  pendingPromise: Promise<string> | null
): Promise<string> => {
  if (existingCatalogId) {
    return existingCatalogId;
  }
  if (pendingPromise !== null) {
    return pendingPromise;
  }
  return createCatalogPromise();
};

/**
 * Normalizes mixed input of File and FileUploadItem into a consistent shape.
 * Extracted as a pure function for testability.
 */
export const normalizeFileInput = (
  input: Array<File | FileUploadItem>
): Array<{ file: File; imageId?: string }> =>
  input.map(fileOrItem => {
    const isFileUploadItem =
      typeof fileOrItem === 'object' &&
      fileOrItem !== null &&
      'file' in fileOrItem &&
      'imageId' in fileOrItem;

    if (isFileUploadItem) {
      return { file: (fileOrItem as FileUploadItem).file, imageId: (fileOrItem as FileUploadItem).imageId };
    }
    return { file: fileOrItem as File, imageId: undefined };
  });

export interface UseMultipartUploadReturn {
  uploadFiles: (files: Array<File | FileUploadItem>, catalogId?: string, ownerId?: string | null) => void;
  uploadQueue: UploadItem[];
  cancelUpload: (fileId: string) => void;
  retryUpload: (fileId: string) => void;
  clearCompleted: () => void;
  removeItem: (fileId: string) => void;
}

const MAX_CONCURRENT_UPLOADS = 3;

/**
 * Pure mapping from the server-reported (and client-synthesized 'TIMEOUT')
 * processing status to the client-side UploadItem status the wizard tile renders.
 *
 * Falls through to currentStatus when the server signal is non-terminal
 * ('UPLOADING') or absent — otherwise late-arriving UPLOADING polls would
 * roll a client item back from 'processing' to 'uploading'.
 *
 * Exported so the mapping decisions can be unit-tested without rendering the hook.
 */
export const mapServerStatusToClientStatus = (
  serverStatus: string | undefined,
  currentStatus: UploadItem['status'],
): UploadItem['status'] => {
  switch (serverStatus) {
    case 'READY':
      return 'completed';
    case 'FAILED':
      return 'failed';
    case 'PROCESSING':
      return 'processing';
    case 'TIMEOUT':
      return 'processing-timeout';
    default:
      return currentStatus;
  }
};

export const useMultipartUpload = (
  ownerId?: string | null,
  catalogAliId?: string | null,
): UseMultipartUploadReturn => {
  const [uploadQueue, setUploadQueue] = useState<UploadItem[]>([]);
  const activeUploadsRef = useRef<Set<string>>(new Set());
  // Coordinates catalog creation across concurrent uploads (issue #858).
  // When multiple images are uploaded simultaneously without an existing catalogId,
  // only the first upload creates a new catalog. This promise is set by the first
  // upload and awaited by all subsequent concurrent uploads so they add images to
  // the same catalog rather than each creating their own separate catalog item.
  const pendingCatalogIdRef = useRef<Promise<string> | null>(null);
  const { processingStatuses, startPolling } = useProcessingStatus();

  // Update upload items based on processing status changes
  // Uses per-image statuses when available (#662), falls back to catalog-level status
  useEffect(() => {
    setUploadQueue(prev =>
      prev.map(item => {
        if (item.catalogId && processingStatuses[item.catalogId]) {
          const processingStatus = processingStatuses[item.catalogId];

          // Try per-image status first (fixes #662: each image gets its own thumbnail)
          const imageStatus = item.imageId && processingStatus.imageStatuses
            ? processingStatus.imageStatuses.find(s => s.imageId === item.imageId)
            : undefined;

          const effectiveThumbnailUrl = imageStatus?.thumbnailUrl || processingStatus.thumbnailUrl;
          const effectiveStatus = imageStatus?.status || processingStatus.status;

          const newStatus = mapServerStatusToClientStatus(effectiveStatus, item.status);

          return {
            ...item,
            status: newStatus,
            progress: effectiveStatus === 'READY' ? 100 :
                     effectiveStatus === 'PROCESSING' ? processingStatus.progress : item.progress,
            thumbnailUrl: effectiveThumbnailUrl,
            error: processingStatus.error,
          };
        }
        return item;
      })
    );
  }, [processingStatuses]);

  const updateUploadItem = useCallback((id: string, updates: Partial<UploadItem>) => {
    setUploadQueue(prev =>
      prev.map(item =>
        item.id === id ? { ...item, ...updates } : item
      )
    );
  }, []);

  const processUpload = useCallback(async (item: UploadItem) => {
    if (activeUploadsRef.current.size >= MAX_CONCURRENT_UPLOADS) {
      return;
    }

    // Prevent duplicate processing
    if (activeUploadsRef.current.has(item.id)) {
      return;
    }

    activeUploadsRef.current.add(item.id);
    updateUploadItem(item.id, { status: 'uploading' });

    // Track timing for the entire upload process
    const timings = {
      initiation: Date.now(),
      urlGeneration: null as number | null,
      uploadStart: null as number | null,
      uploadEnd: null as number | null,
      completion: null as number | null
    };

    // Track whether this upload is responsible for creating the shared catalog ID promise.
    // If the catalog-creating upload fails, we must reset pendingCatalogIdRef so subsequent
    // uploads (retries or new drops) are not permanently blocked by a stale rejected promise.
    // See issue #985 for the regression this prevents.
    let isCatalogCreator = false;

    try {
      // Coordinate catalog creation across concurrent uploads (#858).
      // When multiple files are selected at once with no existing catalogId:
      // - First upload: calls initiateMultipartUpload with no catalogId (creates new catalog)
      //   and stores a promise for the catalogId so concurrent uploads can reuse it.
      // - Subsequent concurrent uploads: await the shared catalogId promise, then call
      //   initiateMultipartUpload with that catalogId to add images to the same catalog.
      let uploadSession;
      if (item.catalogId) {
        // Existing catalog provided (e.g., edit mode): initiate normally.
        // catalogAliId is intentionally NOT forwarded here — the existing
        // catalog already has its owner_ali_id set from the original mint.
        uploadSession = await initiateMultipartUpload(item.file, item.catalogId, item.imageId, ownerId);
      } else if (pendingCatalogIdRef.current !== null) {
        // Another upload already started creating a catalog: wait for its catalogId
        const sharedCatalogId = await pendingCatalogIdRef.current;
        uploadSession = await initiateMultipartUpload(item.file, sharedCatalogId, item.imageId, ownerId);
      } else {
        // First upload without a catalogId: create the catalog and share the catalogId promise.
        // catalogAliId steers WHICH owned catalog (primary vs. add-on) the new
        // item's number is minted under. Only meaningful on the catalog-creating call.
        isCatalogCreator = true;
        const sessionPromise = initiateMultipartUpload(item.file, undefined, item.imageId, ownerId, catalogAliId);
        pendingCatalogIdRef.current = sessionPromise.then(session => session.catalogId);
        uploadSession = await sessionPromise;
      }
      timings.urlGeneration = Date.now();

      updateUploadItem(item.id, {
        uploadId: uploadSession.uploadId,
        catalogId: uploadSession.catalogId,
        imageId: uploadSession.imageId,
        aliCatalogNumber: uploadSession.aliCatalogNumber,
      });

      // Calculate chunks
      const chunkSize = calculateOptimalChunkSize(item.file.size);
      const totalChunks = Math.ceil(item.file.size / chunkSize);
      updateUploadItem(item.id, { totalChunks });

      const parts: Array<{ ETag: string; PartNumber: number }> = [];
      let uploadedBytes = 0;
      const startTime = Date.now();
      timings.uploadStart = startTime;

      // Upload chunks with progress tracking
      for (let i = 0; i < totalChunks; i++) {
        // Check if upload was cancelled
        const currentItem = uploadQueue.find(queueItem => queueItem.id === item.id);
        if (currentItem?.status === 'cancelled') {
          throw new Error('Upload cancelled');
        }

        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, item.file.size);
        const chunk = item.file.slice(start, end);

        // Get presigned URL for this chunk from the upload session
        const presignedUrl = uploadSession.presignedUrls?.find(url => url.partNumber === i + 1);
        if (!presignedUrl) {
          throw new Error(`No presigned URL found for part ${i + 1}`);
        }
        const uploadUrl = presignedUrl.url;

        const etag = await uploadChunk(chunk, uploadUrl, (progress) => {
          const chunkProgress = (progress / 100) * chunk.size;
          const currentUploadedBytes = uploadedBytes + chunkProgress;
          const overallProgress = Math.min((currentUploadedBytes / item.file.size) * 100, 100);

          // Calculate speed and ETA with smoothing
          const elapsedTime = Math.max((Date.now() - startTime) / 1000, 0.1);
          const speed = currentUploadedBytes / elapsedTime;
          const remainingBytes = Math.max(item.file.size - currentUploadedBytes, 0);
          const eta = speed > 0 ? remainingBytes / speed : 0;

          updateUploadItem(item.id, {
            progress: overallProgress,
            uploadedBytes: currentUploadedBytes,
            uploadSpeed: speed,
            estimatedTimeRemaining: eta,
          });
        }).catch(error => {
          // Handle 403 errors that indicate stale upload session
          if (error.response?.status === 403) {
            throw new Error('Upload session expired. Please retry the upload.');
          }
          throw error;
        });

        parts.push({ ETag: etag, PartNumber: i + 1 });
        uploadedBytes += chunk.size;
        updateUploadItem(item.id, { completedChunks: i + 1 });
      }

      timings.uploadEnd = Date.now();

      // Complete multipart upload
      await completeMultipartUpload(uploadSession.uploadId, uploadSession.catalogId, uploadSession.imageId, parts, ownerId);
      timings.completion = Date.now();

      // S3 multipart upload finished, but the Lambda image processor has not
      // generated renditions yet. Stay in 'processing' until polling reports
      // READY (or TIMEOUT) — only then does the wizard tile have a real
      // thumbnailUrl to display. Without this gate, the tile tries to render
      // a blob URL of the original file, which fails decode for TIFF/RAW and
      // surfaces "No Image Available" even on a successful upload.
      updateUploadItem(item.id, {
        status: 'processing',
        progress: 100,
        uploadedBytes: item.file.size
      });

      // Start polling for processing status to get thumbnailUrl and the
      // eventual READY → 'completed' (or TIMEOUT → 'processing-timeout') flip.
      if (uploadSession.catalogId) {
        startPolling(uploadSession.catalogId, ownerId);
      }

    } catch (error) {
      // If this upload was responsible for creating the shared catalog ID promise and it failed,
      // reset pendingCatalogIdRef so subsequent uploads (retries or new drop events) can attempt
      // fresh catalog creation rather than awaiting a stale rejected promise (#985).
      if (isCatalogCreator) {
        pendingCatalogIdRef.current = null;
      }

      logger.error('Upload failed for file', {
        fileId: item.id,
        filename: item.file.name,
        uploadId: item.uploadId?.substring(0, 20) + '...',
        catalogId: item.catalogId,
        imageId: item.imageId,
        error: {
          message: error instanceof Error ? error.message : String(error),
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          code: error?.code,
          data: error?.response?.data
        },
        timings: {
          totalDuration: Date.now() - timings.initiation,
          failedAt: timings.uploadStart ? 'upload' : timings.urlGeneration ? 'initiation' : 'setup'
        },
        timestamp: new Date().toISOString()
      });

      let errorMessage = 'Upload failed';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error?.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      updateUploadItem(item.id, {
        status: errorMessage === 'Upload cancelled' ? 'cancelled' : 'failed',
        error: errorMessage
      });
    } finally {
      activeUploadsRef.current.delete(item.id);
      // Trigger processing of next item via state update
      setUploadQueue(prev => [...prev]);
    }
  }, [updateUploadItem, uploadQueue, startPolling]);

  // Auto-process next uploads when queue changes
  useEffect(() => {
    const pendingItem = uploadQueue.find(item =>
      item.status === 'pending' && !activeUploadsRef.current.has(item.id)
    );

    if (pendingItem && activeUploadsRef.current.size < MAX_CONCURRENT_UPLOADS) {
      processUpload(pendingItem);
    }
  }, [uploadQueue, processUpload]);

  const uploadFiles = useCallback((files: Array<File | FileUploadItem>, catalogId?: string) => {
    const normalized = normalizeFileInput(files);

    const newItems: UploadItem[] = normalized.map(({ file, imageId }) => ({
      id: uuidv4(),
      file,
      imageId,
      catalogId,
      status: 'pending' as const,
      progress: 0,
      uploadedBytes: 0,
      totalChunks: 0,
      completedChunks: 0,
      uploadSpeed: 0,
      estimatedTimeRemaining: 0,
    }));

    setUploadQueue(prev => [...prev, ...newItems]);

    // Start processing uploads
    setTimeout(() => {
      newItems.forEach(item => {
        if (activeUploadsRef.current.size < MAX_CONCURRENT_UPLOADS) {
          processUpload(item);
        }
      });
    }, 0);
  }, [processUpload]);

  const cancelUpload = useCallback((fileId: string) => {
    setUploadQueue(prev =>
      prev.map(item => {
        if (item.id === fileId && item.status !== 'completed') {
          activeUploadsRef.current.delete(fileId);
          return { ...item, status: 'cancelled' as const };
        }
        return item;
      })
    );
  }, []);

  const retryUpload = useCallback((fileId: string) => {
    setUploadQueue(prev => {
      const updatedQueue = prev.map(item => {
        if (item.id === fileId && item.status === 'failed') {
          return {
            ...item,
            status: 'pending' as const,
            progress: 0,
            uploadedBytes: 0,
            completedChunks: 0,
            uploadSpeed: 0,
            estimatedTimeRemaining: 0,
            error: undefined,
            uploadId: undefined,
            // Preserve catalogId and imageId for idempotent retry
            thumbnailUrl: undefined,
            aliCatalogNumber: undefined,
          };
        }
        return item;
      });

      // Process the retried upload
      setTimeout(() => {
        const item = updatedQueue.find(item => item.id === fileId);
        if (item && item.status === 'pending' && activeUploadsRef.current.size < MAX_CONCURRENT_UPLOADS) {
          processUpload(item);
        }
      }, 0);

      return updatedQueue;
    });
  }, [processUpload]);

  const clearCompleted = useCallback(() => {
    setUploadQueue(prev => prev.filter(item => item.status !== 'completed' && item.status !== 'processing'));
  }, []);

  const removeItem = useCallback((fileId: string) => {
    setUploadQueue(prev => prev.filter(item => item.id !== fileId));
    activeUploadsRef.current.delete(fileId);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    const activeUploads = activeUploadsRef.current;
    return () => {
      activeUploads.clear();
    };
  }, []);

  return {
    uploadFiles,
    uploadQueue,
    cancelUpload,
    retryUpload,
    clearCompleted,
    removeItem,
  };
};
